import React from 'react';
import { getCdnScriptUrl, toSsoLoginPage } from '@/components/Biz/IYB_Util/IYB_Util';
import Layout from '@/layout/Layout';
import {
  getCurrent,
  getEnterpriseCode,
  getMainSystemEnvInfo,
  getMenuTree,
  logout,
} from '@/services/service';
import { MenuDataTyping } from '@/services/userInterface';
import { API } from '@/types/typings';
import { defaultSystemInfo } from '@/util/system.data';
import { isMenuLocked, normalizeMenuAccess } from '@/util/utils';
import { createFromIconfontCN, LockOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { history, Link, RequestConfig } from '@umijs/max';
import { MenuDataItem } from '@umijs/route-utils';
import { notification, Tooltip } from 'antd';
import { isMobile } from 'react-device-detect';
import { RunTimeLayoutConfig } from 'umi';
import BASEURLCONFIG, { BaseRouteUrl } from '../config/BASEURLCONFIG';
import defaultSettings from '../config/defaultSettings';
import { AvatarDropdown } from './components/RightContent/AvatarDropdown';
import UpgradePrompt from './components/UpgradePrompt';
import { getAppToken, getPlatformInfo } from '@/services/h5Service';
import { AppVersionList, getAppVersionConfig, getProductVersionTitle } from '@/utils/commonDict';
import { appendRegCodeToUrl, getActiveRegCode } from '@/util/regCode';
import { getVersionTrialCurrent } from '@/pages/VersionTrial/service';
import { canUseCustomerVersionTrialApi, isPlatformAdminUser } from '@/utils/versionTrialContext';

// 检查是否配置了baseUrl，如果配置了，那么getNowPath()应该剔除这部分
const getNowPath = () => {
  return !!BaseRouteUrl
    ? history.location.pathname.replace(BaseRouteUrl, '')
    : history.location.pathname;
};
// 是否处于无权限验证的页面（即未登录也能查看）
const testNoAuthPath = (path: string) => {
  return path === '' || path === '/' || path === '/welcome' || path.startsWith('/user');
};

export interface EnvInfo {
  envSuccess?: string;
  start?: string;
  autoStopped?: string;
  autoRestarting?: string;
  enterpriseId?: number;
  main_system?: string;
  env?: string;
  iyb?: string;
  code?: string;
  enterpriseRoleCode?: string;
  enterpriseRoleName?: string;
  versionTrialStatus?: string;
  versionTrialStartTime?: string;
  versionTrialEndTime?: string;
}

export interface InitialState {
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  menuData?: MenuDataTyping[];
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  envInfo?: EnvInfo;
  AppVersion?: string;
}

const defaultAppVersion = AppVersionList?.Enterprise?.code;
const lockedMenuMessage = '尊享版专属功能，开通后即可使用';

const renderLockedMenuItem = (defaultDom: React.ReactNode, showLockIcon = true) => {
  return (
    <Tooltip title={lockedMenuMessage}>
      <div className="custom-menu-lock-title">
        {defaultDom}
        {showLockIcon ? <LockOutlined className="custom-menu-lock-icon" /> : null}
      </div>
    </Tooltip>
  );
};

const getAppVersionCode = (envInfo?: EnvInfo) => {
  const versionCode = envInfo?.enterpriseRoleCode || envInfo?.code;
  const version = getAppVersionConfig(versionCode);
  if (version) {
    return version.code;
  }
  return defaultAppVersion;
};

const versionTrialAdminMenu: MenuDataItem = {
  name: '试用情况',
  path: '/versionTrialAdmin',
};

/**
 * 判断菜单树中是否已经存在可见的指定路径，避免平台菜单重复插入。
 */
const hasVisibleMenuItemByPath = (menuData: MenuDataItem[] = [], path: string): boolean => {
  return menuData.some((item) => {
    const children = ((item as any).children || item.routes || []) as MenuDataItem[];
    return (item.path === path && !item.hideInMenu) || hasVisibleMenuItemByPath(children, path);
  });
};

/**
 * 将试用情况菜单插入到平台维护的企业数据监测后方。
 */
const insertVersionTrialAdminMenu = (menuData: MenuDataItem[]): MenuDataItem[] => {
  if (hasVisibleMenuItemByPath(menuData, versionTrialAdminMenu.path || '')) {
    return menuData;
  }

  let inserted = false;
  const insertAfterEnterpriseDataMonitor = (items: MenuDataItem[] = []): MenuDataItem[] => {
    const nextItems: MenuDataItem[] = [];
    items.forEach((item) => {
      const nextItem = { ...item } as MenuDataItem & { children?: MenuDataItem[] };
      if (item.routes?.length) {
        nextItem.routes = insertAfterEnterpriseDataMonitor(item.routes as MenuDataItem[]);
      }
      if (nextItem.children?.length) {
        nextItem.children = insertAfterEnterpriseDataMonitor(nextItem.children);
      }
      nextItems.push(nextItem);
      if (item.path === '/base/enterprise/dataMonitor' && !inserted) {
        inserted = true;
        nextItems.push({
          ...versionTrialAdminMenu,
          icon: item.icon,
        });
      }
    });
    return nextItems;
  };

  const nextMenuData = insertAfterEnterpriseDataMonitor(menuData);
  return inserted ? nextMenuData : [...nextMenuData, versionTrialAdminMenu];
};

/*
 * getEnvInfo 是企业环境配置接口，不一定能立刻反映试用状态：
 * - 申请试用后，后端菜单/版本切换可能已经生效，但前端 initialState 仍是旧版本。
 * - 手工改数据库或到期时，envInfo 也可能短时间显示旧的尊享版状态。
 * 所以启动时额外调用 versionTrial/current，以平台试用记录修正前端当前版本展示。
 */
const mergeVersionTrialEnvInfo = async (envInfo?: EnvInfo, currentUser?: API.CurrentUser) => {
  if (!envInfo) {
    return envInfo;
  }

  // 只有普通用户端企业上下文才允许查询试用状态，平台管理员和无企业上下文直接跳过。
  if (!canUseCustomerVersionTrialApi(currentUser, envInfo)) {
    return envInfo;
  }

  try {
    const trialInfo = await getVersionTrialCurrent();
    if (trialInfo?.trialActive) {
      const versionCode = trialInfo.trialVersionCode || AppVersionList.Professional.code;
      const versionName = trialInfo.trialVersionName || AppVersionList.Professional.name;
      return {
        ...envInfo,
        code: versionCode,
        enterpriseRoleCode: versionCode,
        enterpriseRoleName: versionName,
        versionTrialStatus: trialInfo.trialStatus,
        versionTrialStartTime: trialInfo.trialStartTime,
        versionTrialEndTime: trialInfo.trialEndTime,
      };
    }

    if (trialInfo?.trialStatus === 'EXPIRED') {
      /*
       * 后端 current() 会幂等回滚；前端也按已过期状态即时恢复免费版展示。
       * 这样即使菜单数据还没刷新，顶部版本和升级弹窗也不会继续误显示“试用中”。
       */
      return {
        ...envInfo,
        code: AppVersionList.PREVIEW_ENV.code,
        enterpriseRoleCode: AppVersionList.PREVIEW_ENV.code,
        enterpriseRoleName: AppVersionList.PREVIEW_ENV.name,
        versionTrialStatus: trialInfo.trialStatus,
        versionTrialStartTime: trialInfo.trialStartTime,
        versionTrialEndTime: trialInfo.trialEndTime,
      };
    }
  } catch (error) {
    console.log('mergeVersionTrialEnvInfo error', error);
  }

  return envInfo;
};

// 检测当前域名是否为 IP 地址或 localhost
const isIPOrLocalhost = (hostname: string) => {
  console.log('hostname', hostname);
  return hostname === 'localhost' || /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);
};

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<InitialState> {
  getActiveRegCode();
  let isH5 =
    isMobile &&
    (history.location.pathname.startsWith('/h5') ||
      history.location.pathname.startsWith('/edatlink-admin/h5'));
  if (isH5) {
    let resForCurrent = await getCurrent({ skipErrorHandler: true });
    console.log('h5-获取currentUser', resForCurrent);
    return {
      currentUser: resForCurrent,
    };
  } else {
    let defaultSettingsTemp = defaultSettings;
    let envInfo: EnvInfo | undefined;

    // 首先尝试从域名获取enterpriseCode
    let enterpriseCode = location.hostname
      .replace('iswan.cc', '')
      .replace('prod', '')
      .substring(0, location.hostname.indexOf('.'));
    if (process.env.NODE_ENV === 'development') {
      enterpriseCode = BASEURLCONFIG.enterpriseCodeForDev;
    } else {
      // to-do 检测域名是 localhost 或者 IP ，如果是，先从 localstorage 中获取，
      const showEnterpriseCode = isIPOrLocalhost(location.hostname);
      if (showEnterpriseCode) {
        const ENTERPRISE_CODE_KEY = 'edatlink_enterprise_code';
        enterpriseCode = localStorage.getItem(ENTERPRISE_CODE_KEY) || '';
      }
    }

    const fetchUserInfo = async () => {
      createFromIconfontCN({
        scriptUrl: getCdnScriptUrl(),
      });

      if (localStorage.getItem('fromLoginEvent') !== 'Y') {
        if (location.href.indexOf('/user/login') > -1) {
          return undefined;
        }
      }

      try {
        let resForCurrent = await getCurrent({ skipErrorHandler: true });

        if (resForCurrent) {
          let enterpriseCodeReal = resForCurrent.enterpriseCode;

          // 如果用户已登录，使用用户的企业编码获取envInfo
          envInfo = await getMainSystemEnvInfo({ enterpriseCode: enterpriseCodeReal });
          envInfo = await mergeVersionTrialEnvInfo(envInfo, resForCurrent);

          if (!envInfo) {
            history.push('/prepareEnv');
            console.log(resForCurrent, 'resForCurrent');
            return {
              ...resForCurrent,
              menuData: [],
            };
          }

          if (!envInfo || envInfo.envSuccess !== 'Y' || envInfo.autoStopped === 'Y' || envInfo.autoRestarting === 'Y') {
            history.push('/processEnv/' + enterpriseCodeReal);
            return undefined;
          }

          // 检查是否需要重定向到主系统
          if (envInfo.code === 'PREVIEW_ENV') {
            if (location.host === 'iswan.cc' || location.host === 'platform.prod.iswan.cc') {
              //查询已经开通的企业
              let res = await getEnterpriseCode({ mobile: resForCurrent.mobile });
              if (res !== 'platform') {
                let host = location.host.replace('platform', '');

                if (host === 'iswan.cc') {
                  host = 'prod.iswan.cc';
                }
                let redirectUrl =
                  'https://' +
                  enterpriseCodeReal +
                  '.' +
                  host +
                  '/edatlink-admin/user/login?autoLogin=Y&enterpriseCode=' +
                  enterpriseCodeReal;
                redirectUrl = appendRegCodeToUrl(redirectUrl);
                window.location.href = redirectUrl;
                return undefined;
              }
            }
          }

          let resForMenuTree = await getMenuTree({
            sort: 'sortNo desc',
            queryData: { type: 0, includeApp: 'Y', terminalType: 'WEB' },
          });
          let menuData =
            resForMenuTree?.filter((item: any) => item.appCode !== 'ISWAN-DATA-DECISION') ||
            resForMenuTree;
          menuData = normalizeMenuAccess(menuData);

          if (envInfo.iyb === 'Y') {
            defaultSettingsTemp.title = ' 智能体';
            defaultSettingsTemp.logo = BaseRouteUrl + '/image/iyb_icon.png';
          }
          return {
            ...resForCurrent,
            menuData: menuData,
          };
        }
      } catch (error) {
        console.log(error, 'error');
        // history.push(loginPath);
      }
      return undefined;
    };

    console.log(142.111, 'envInfo', envInfo);
    // 如果用户未登录，使用域名中的enterpriseCode获取envInfo
    if (!envInfo && enterpriseCode) {
      envInfo = await getMainSystemEnvInfo({ enterpriseCode });
      if (!envInfo || envInfo.envSuccess !== 'Y' || envInfo.autoStopped === 'Y' || envInfo.autoRestarting === 'Y') {
        history.push('/processEnv/' + enterpriseCode);
        return {
          envInfo,
          settings: defaultSettingsTemp as Partial<LayoutSettings>,
        };
      }
    }

    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      menuData: currentUser?.menuData ?? [],
      settings: defaultSettingsTemp as Partial<LayoutSettings>,
      envInfo,
      AppVersion:
        getAppVersionCode(envInfo),
    };
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const setActiveAppCode = (location, initialState) => {
  if (!location?.pathname || !initialState?.menuData) return;
  // 查找当前路径对应的一级菜单
  const firstLevelMenu = initialState.menuData.find((menu) => {
    const routes = menu?.routes;
    // 通过匹配子路由,获取appCode
    return Array.isArray(routes) && routes.find((item) => location?.pathname.includes(item.path));
  });
  console.log(164.111, firstLevelMenu, location, initialState, history);
  if (firstLevelMenu) {
    const currentSearchParams = new URLSearchParams(history.location.search);
    // 如果当前没有appType参数或参数值与菜单不匹配，则更新
    if (
      !currentSearchParams.get('appCode') ||
      currentSearchParams.get('appCode') !== firstLevelMenu.appCode
    ) {
      currentSearchParams.set('appCode', firstLevelMenu.appCode);
      window.sessionStorage.setItem('IsWan_Admin_Box_CurrentAppCode', firstLevelMenu.appCode);
      history.replace({
        pathname: location.pathname,
        search: currentSearchParams.toString(),
      });
    }
  }
};

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  let currentLogo = initialState?.settings?.logo || defaultSystemInfo.systemLogo;
  const currentTheme =
    localStorage.getItem(`${initialState?.currentUser?.mobile}_edatlink_box_admin_theme`) || '';
  const currentUserName = initialState?.currentUser?.nickName || initialState?.currentUser?.account;
  if (['black', 'blue'].includes(currentTheme)) {
    currentLogo = BaseRouteUrl + '/image/edatlink_logo_light.svg';
  }
  // 排除logo避免被覆盖
  const otherSettings = { ...(initialState?.settings || {}) };
  delete otherSettings.logo;
  return {
    title: getProductVersionTitle(initialState?.AppVersion as string),
    logo: currentLogo,
    headerTitleRender: (logo) => <a className="edatlink-header-brand">{logo}</a>,
    avatarProps: {
      title: currentUserName,
      render: () => {
        return (
          <AvatarDropdown>
            <div>
              <span>{currentUserName}</span>
            </div>
          </AvatarDropdown>
        );
      },
    },
    actionsRender: () => [],
    // headerRender:false,
    headerRender: (props, defaultDom) => {
      return <div className={`custom-header-box`}> {defaultDom} </div>;
    },
    onPageChange: (location?: { pathname?: string }) => {
      // 返回appCode给工作台
      // setActiveAppCode(location,initialState);
      if (!location?.pathname) return;

      if (
        location.pathname.startsWith('/user/login') ||
        location.pathname.startsWith('/app/login')
      ) {
        logout();
        return;
      }
      if (location.pathname.startsWith('/processEnv')) {
        return;
      }
      if (location.pathname.startsWith('/singleEnv')) {
        console.log('singleEnv');
        return;
      }
      if (
        isMobile &&
        (location.pathname.startsWith('/h5') || location.pathname.startsWith('/edatlink-admin/h5'))
      ) {
        console.log('/h5--', location);
        return;
      }
      // 如果没有登录，重定向到 login
      console.log(initialState?.currentUser, 'initialState.currentUser');
      console.log(!initialState?.currentUser, '!(initialState?.currentUser)');
      if (!initialState?.currentUser) {
        console.log(111111111111);
        // history.push(loginPath);
        return;
      }
      if (
        !initialState?.envInfo &&
        !location.pathname.startsWith('/market/order/order/orderDetail')
      ) {
        history.push('/prepareEnv');
        return;
      }

      if (isMobile && !location?.pathname?.startsWith('/app')) {
        history.push('/app/guide');
      }

      if (testNoAuthPath(getNowPath())) {
        return;
      }
      // 开发环境不检测后台配置的url
      if (process.env.NODE_ENV === 'development') {
        return;
      }
    },
    menu: {
      locale: false,
      type: 'group',
      collapsedShowTitle: true,
    },
    menuItemRender: (menuItemProps, defaultDom) => {
      const currentMenuItem = menuItemProps as MenuDataTyping & {
        isUrl?: boolean;
        children?: unknown;
        target?: string;
        path?: string;
      };

      if (isMenuLocked(currentMenuItem)) {
        const lockedDom = renderLockedMenuItem(defaultDom);

        if (currentMenuItem.isUrl || currentMenuItem.children) {
          return lockedDom;
        }

        if (currentMenuItem.path && history.location.pathname !== currentMenuItem.path) {
          return (
            <Link to={currentMenuItem.path.replace('/*', '')} target={currentMenuItem.target}>
              {lockedDom}
            </Link>
          );
        }

        return lockedDom;
      }

      if (currentMenuItem.isUrl || currentMenuItem.children) {
        return defaultDom;
      }

      if (currentMenuItem.path && history.location.pathname !== currentMenuItem.path) {
        return (
          <Link to={currentMenuItem.path.replace('/*', '')} target={currentMenuItem.target}>
            {defaultDom}
          </Link>
        );
      }

      return defaultDom;
    },
    subMenuItemRender: (menuItemProps, defaultDom) => {
      if (isMenuLocked(menuItemProps as MenuDataTyping)) {
        return renderLockedMenuItem(defaultDom, false);
      }

      return defaultDom;
    },
    menuProps: {
      className: 'custom-menu-box',
    },
    siderWidth: 166,
    contentStyle: { padding: 0 },
    childrenRender: (children) => {
      return (
        <>
          <Layout>{children}</Layout>
          <UpgradePrompt />
        </>
      );
    },
    menuDataRender: (menuData): MenuDataItem[] => {
      console.log(location.pathname, 1111);
      if (
        location.pathname.indexOf('/edatlink-admin/workbenchAdmin/admin') > -1 ||
        location.pathname === '/edatlink-admin/'
      ) {
        return [];
      }
      // if (process.env.NODE_ENV === 'development') {
      //   return menuData;
      // }
      const renderedMenuData = (initialState?.menuData as MenuDataItem[] | undefined) || menuData || [];
      if (!isPlatformAdminUser(initialState?.currentUser)) {
        return renderedMenuData;
      }
      /*
       * 试用情况是平台管理员视图，不依赖企业端后端菜单返回。
       * 平台账号 enterpriseId=1 / enterpriseCode=platform 时插入到企业数据监测下方，普通企业账号不展示。
       */
      return insertVersionTrialAdminMenu(renderedMenuData);
    },
    ...otherSettings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
// export const request = {
//   ...errorConfig,
// };

const normalizeRequestParams = (params?: any) => {
  if (!params || typeof params !== 'object') {
    return params;
  }

  if (params.queryData && typeof params.queryData === 'object') {
    return {
      ...params,
      queryData: JSON.stringify(params.queryData),
    };
  }

  return params;
};

export const request: RequestConfig = {
  requestInterceptors: [
    async (url, options) => {
      // console.log('url', url);
      options['credentials'] = 'include';
      options.params = normalizeRequestParams(options.params);
      // 如果是手机端，可以额外处理（可选）
      if (isMobile) {
        const appToken = await getAppToken();
        if (typeof appToken === 'string' && appToken) {
          // 在 Header 中添加 Authorization
          options.headers = {
            ...options.headers,
            // 添加 Bearer 前缀
            Authorization: `${appToken}`,
          };
        }
      }
      return { url, options };
    },
  ],
  responseInterceptors: [
    async (response) => {
      const { data = {} as any, config = {} as any } = response;
      if (config?.url?.indexOf('api/office/export') > -1) {
        return response;
      }
      if (data.code === 0) {
        return data;
      } else {
        if (data.code === 99998) {
          // console.log(history.location.pathname,'history.location.pathname');
          // console.log(config?.url,'config?.url');
          if (isMobile && history.location.pathname.indexOf('/login') === -1) {
            if (
              history.location.pathname.indexOf('/h5/noAuth') === -1 ||
              history.location.pathname.indexOf('/app/guide') === -1
            ) {
              const platformInfo = await getPlatformInfo();
              console.log('测试platformInfo', platformInfo?.appName);
              if (platformInfo?.appName === 'edatlink_app') {
                history.push('/h5/noAuth');
              } else {
                toSsoLoginPage();
              }
              return;
            } else {
              return;
            }
            // const message = data.msg.startsWith('[') ? JSON.parse(data.msg)[0] : data.msg;
            // notification.error({
            //   message
            // });
          }
          if (
            history.location.pathname.indexOf('/login') === -1 &&
            history.location.pathname.indexOf('/forgetPassword') === -1 &&
            history.location.pathname.indexOf('/resetPassword') === -1
          ) {
            toSsoLoginPage();
          }
          return false;
        } else {
          if (data.msg && !config?.skipErrorHandler) {
            notification.error({
              message: data.msg.startsWith('[') ? JSON.parse(data.msg)[0] : data.msg,
            });
            throw {
              success: false,
            };
          }
        }
      }
      return response;
    },
  ],
};
